/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import MainNavigation from "@/components/layout/main-navigation";
import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import { MarketingSectionHeader } from "@/components/landing/marketing-section-header";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import { Shield, Lock, Server, Eye, CheckCircle } from "lucide-react";
import { EMAILS } from "@/lib/constants/master";
import { FadeIn } from "@/components/landing/primitives/fade-in";

const securityFeatures = [
  {
    icon: Shield,
    title: "Security-First Architecture",
    description:
      "Row-level security policies, input validation, and output sanitization built into every layer of the application.",
  },
  {
    icon: Lock,
    title: "Encryption in Transit & at Rest",
    description:
      "All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption via our infrastructure providers.",
  },
  {
    icon: Server,
    title: "Secure Infrastructure",
    description:
      "Built on SOC 2 certified infrastructure (Vercel, Supabase) with automated backups and monitoring.",
  },
  {
    icon: Eye,
    title: "Access Controls",
    description:
      "Role-based access controls, multi-factor authentication, and detailed audit logs for all account activity.",
  },
];

const securityPractices = [
  "Dependency vulnerability monitoring via Dependabot",
  "Data backup and retention policies",
  "Secure development practices and code review",
];

export function SecurityPageClient() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <MainNavigation />

      <main className="flex-1">
        <MarketingPageHero
          variant="dark"
          badge="Security"
          title={
            <>
              Serious{" "}
              <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
                Security
              </span>
            </>
          }
          description="Your business data is encrypted in transit and at rest. Role-based access controls who sees what. We never sell or share your information."
        />

        {/* Security Features Grid */}
        <section
          className="py-16 md:py-24 lg:py-32 bg-background"
          aria-labelledby="security-features"
        >
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
            <MarketingSectionHeader
              badge="Protection"
              title="How we keep your data safe"
              description="Multiple layers of security protect your business information at every level."
            />

            <div className="grid gap-6 md:grid-cols-2">
              {securityFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <FadeIn
                    key={feature.title}
                    as="article"
                    className="card-premium rounded-2xl border border-border bg-card p-6"
                    delay={index * 100}
                  >
                    <div
                      className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-secondary"
                      aria-hidden="true"
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="mb-2 text-lg font-bold text-foreground font-display">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>

        {/* Security Practices */}
        <section
          className="py-16 md:py-24 lg:py-32 bg-background-warm"
          aria-labelledby="security-practices"
        >
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
            <MarketingSectionHeader
              badge="Practices"
              title="Our Security Practices"
              description="Rigorous processes ensure your data stays protected."
            />

            <ul className="max-w-2xl mx-auto space-y-4">
              {securityPractices.map((practice, index) => (
                <FadeIn
                  key={practice}
                  as="li"
                  className="flex items-start gap-3"
                  delay={index * 80}
                >
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-success" />
                  <span className="text-foreground">{practice}</span>
                </FadeIn>
              ))}
            </ul>
          </div>
        </section>

        <MarketingCTASection
          title="Security questions?"
          description="If you have questions about our security practices or need to report a security concern, please contact our security team."
          primaryCTA={{
            text: "Contact Security Team",
            href: `mailto:${EMAILS.SECURITY}`,
          }}
          secondaryCTA={{
            text: "Start Your Free Trial",
            href: "/auth/sign-up",
          }}
          trustIndicators={[
            "Built on SOC 2 certified infrastructure • AES-256 encryption • TLS 1.3",
          ]}
        />
      </main>

      <Footer />
    </div>
  );
}
